import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { TextInput, Button, Container, Paper, Stack, Text } from '@mantine/core';
import PasswordRequirementInput from './common/PasswordRequirementInput';
import { isPasswordValid } from '../utils/passwordValidation';

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (countdown > 0) return;
    
    setIsLoading(true);
    setError({});
    setSuccessMessage('');

    try {
      await resetPassword({ username: email });
      setCodeSent(true);
      setCountdown(30);
      setSuccessMessage('A verification code has been sent to your email');
    } catch (error) {
      setError({top: error.message || 'Failed to send verification code'});
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!isPasswordValid(newPassword)) {
      setError({password: 'Password is missing required characters'});
      return;
    }

    setIsLoading(true);
    setError({});
    setSuccessMessage('');
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword
      });
      setSuccessMessage('Password successfully reset');
      setTimeout(() => {
        setIsLoading(false);
        navigate('/login');
      }, 2000);
    } catch (error) {
      setError({top: error.message || 'Failed to reset password'});
      setIsLoading(false);
    }
  };

  return (
    <Container size={420} my={40}>
      <Paper radius="md" p="xl" withBorder>
        <Text size="lg" fw={500} ta="center" mb="xl">
          Reset Your Password
        </Text>

        <form onSubmit={codeSent ? handleResetPassword : handleSendCode}>
          <Stack>
            {error.top && (
              <Text c="red" size="sm" ta="center">
                {error.top}
              </Text>
            )}
            
            {successMessage && (
              <Text c="green" size="sm" ta="center">
                {successMessage}
              </Text>
            )}

            <TextInput
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              radius="md"
              required
              disabled={codeSent}
            />

            {codeSent && (
              <>
                <TextInput
                  label="Verification Code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter verification code"
                  radius="md"
                  required
                />

                <PasswordRequirementInput
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  error={error.password}
                  placeholder="Enter new password"
                  radius="md"
                  required
                  showRequirements={true}
                />
              </>
            )}

            <Button 
              type="submit" 
              radius="xl"
              loading={isLoading}
              fullWidth
            >
              {codeSent ? 'Reset Password' : 'Send Reset Code'}
            </Button>

            {codeSent && (
              <Button
                variant="transparent"
                onClick={handleSendCode}
                sx={(theme) => ({
                  color: countdown > 0 ? theme.colors.gray[6] : theme.colors.violet[6],
                  fontSize: theme.fontSizes.sm,
                  cursor: countdown > 0 ? 'default' : 'pointer',
                  textAlign: 'center',
                  '&:hover': {
                    textDecoration: countdown > 0 ? 'none' : 'underline',
                  },
                })}
              >
                {countdown > 0 
                  ? `Send new code (${countdown}s)`
                  : 'Send new code'
                }
              </Button>
            )}
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}

export default ForgotPassword; 